import { PHASES } from './types.js';
import type { Phase } from './types.js';

/**
 * The log-line bridge.
 *
 * `apps/crawler/src/pipeline.ts` already emits a precise, stable progress format
 * through its `onProgress` callback, and the most likely thing an onboarding
 * server does is forward those strings straight into the SSE stream. So rather
 * than requiring the server to invent a second, structured probe protocol, the
 * console parses the format the crawler already prints:
 *
 *   ── 5/6 probe (the gate — unverified capabilities are dropped)
 *      PASS  query_brza_kupnja_ajax  rows=61 pass=100%
 *      DROP  query_provjera_kupona_ajax  rows=0 pass=0%  — tried 9 value(s) across 3 vocabular(ies); none returned data
 *
 * Structured fields on the event still win when the server sends them. This is
 * the fallback, and it is the reason the ledger is populated in a live run even
 * if the backend only ever implements the four documented SSE fields.
 */

const PHASE_SET = new Set<string>(PHASES);

/** `── 3/6 brand` → { ordinal: 3, phase: 'brand' } */
export function parseStageHeader(message: string): { ordinal: number; phase: Phase } | null {
  const m = /^\s*(?:──\s*)?(\d)\s*\/\s*6\s+([a-z]+)/.exec(message);
  if (!m) return null;
  const ordinal = Number(m[1]);
  const word = m[2] ?? '';
  if (!PHASE_SET.has(word)) return null;
  return { ordinal, phase: word as Phase };
}

export interface ParsedVerdict {
  id: string;
  verdict: 'cleared' | 'turned-back';
  rows: number;
  passRate: number;
  reason?: string;
}

/**
 * `PASS  list_items  rows=49 pass=100%`
 * `DROP  query_x  rows=0 pass=0%  — tried 9 value(s) across 3 vocabular(ies); none returned data`
 *
 * Both an em dash and a plain hyphen are accepted as the reason separator so a
 * server that normalises punctuation on the way out does not silently lose the
 * single most useful string in the whole stream.
 */
export function parseProbeLine(message: string): ParsedVerdict | null {
  const m =
    /^\s*(PASS|DROP)\s+([a-z][a-z0-9_]*)\s+rows=(\d+)\s+pass=(\d+(?:\.\d+)?)%\s*(?:[—–-]\s*(.+))?$/i.exec(
      message,
    );
  if (!m) return null;
  const reason = m[5]?.trim();
  return {
    id: m[2] ?? '',
    verdict: (m[1] ?? '').toUpperCase() === 'PASS' ? 'cleared' : 'turned-back',
    rows: Number(m[3] ?? 0),
    passRate: Number(m[4] ?? 0) / 100,
    ...(reason ? { reason } : {}),
  };
}

/** `   · list_items (heuristic)` → `list_items (heuristic)` */
export function stripBullet(message: string): string {
  return message.replace(/^\s*[·•*-]\s*/, '').trim();
}

/**
 * Stage detail lines are printed as `   key    value`. The console shows them
 * as a two-column ledger, so the label is split off rather than reflowed.
 */
export function splitDetail(message: string): { label?: string; value: string } {
  const trimmed = message.trim();
  // Case-insensitive because the crawler prints both `origin  …` and `GET  …`,
  // and a ledger where only half the lines find their label reads worse than
  // one where none of them do.
  const m = /^([A-Za-z][A-Za-z0-9._-]{1,11})\s{2,}(.+)$/.exec(trimmed);
  if (m) return { label: m[1] ?? '', value: m[2] ?? '' };
  return { value: trimmed };
}

/** Long JSON blobs in the log (`stack {...}`) are unreadable inline. */
export function clip(value: string, max = 120): string {
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}

/**
 * The synthesize stage's proposal lines.
 *
 *   `   · list_items (heuristic)`      the crawler's own format
 *   `   propose find_showtimes`        the shorter form in the CineStar recording
 *
 * Capturing these is what lets the probe stage say "10 declared, 4 cleared, 6
 * turned back, 0 still running" instead of growing a list from nothing. Before
 * this the operator could not tell a gate that had finished from a gate that
 * had not started, which is the one thing the probe stage exists to communicate.
 */
export function parseProposalLine(message: string): string | null {
  const m =
    /^\s*(?:[·•*-]\s*|propose\s+)([a-z][a-z0-9_]{2,})\s*(?:\((?:heuristic|llm|assisted)\))?\s*$/i.exec(
      message,
    );
  return m?.[1] ?? null;
}

/**
 * `     list_items: 23 row(s)` — the probe's own running commentary, printed
 * once per capability BEFORE the PASS/DROP verdict line for the same id. The
 * verdict says the same thing better, so the ledger drops these rather than
 * printing every capability twice.
 */
export function isProbeChatter(message: string): boolean {
  return /^\s+[a-z][a-z0-9_]*:\s+(?:\d+ row\(s\)|FAILED\b)/i.test(message);
}
